import { IntlShape } from "react-intl";

import { APP_NAME, CLI_COMMAND_NAME } from "Constants/documentationUrls";

import { WizardStep } from "./wizardStepTypes";

export const getDefaultBlankWizardSteps = (
  intl: IntlShape,
  mainBranch: string,
  configurationFolder: string
): WizardStep[] => {
  const copyCodeDescription = () => {
    if (intl.messages["setup.cli.install.code"]) {
      return [
        {
          label: intl.formatMessage({ id: "setup.cli.install.label" }),
          code: [intl.formatMessage({ id: "setup.cli.install.code" })]
        }
      ];
    }
    return [
      {
        label: intl.formatMessage({ id: "setup.cli.install.unix.label" }),
        code: [intl.formatMessage({ id: "setup.cli.install.unix.code" })]
      },
      {
        label: intl.formatMessage({
          id: "setup.cli.install.windows.label"
        }),
        code: [intl.formatMessage({ id: "setup.cli.install.windows.code" })]
      }
    ];
  };

  return [
    {
      id: "cli",
      label: "Download the CLI",
      title: "Download the CLI",
      required: false,
      bodyText: `<p>To install the CLI (Command Line Interface), run the following command.</p><p>For more info about our CLI check out our <a href="${intl.formatMessage(
        { id: "links.documentation.CLI" }
      )}" target="_blank" rel="noopener noreferrer">documentation</a> or take a look at our <a href=${intl.formatMessage(
        { id: "links.CLI_repository" }
      )} target="_blank" rel="noopener noreferrer">CLI source code</a> which provides alternate installation methods.</p>`,
      copyCode: copyCodeDescription()
    },
    {
      id: "remote",
      label: `Set ${APP_NAME} remote`,
      title: `Set ${APP_NAME} as a remote so you can push code to ${APP_NAME}`,
      required: true,
      bodyText: `<p>If you have a local Git repository already available, add your ${APP_NAME} repository as a new remote. If you have not already initialized your project directory as a Git repository, you will need to do this first.</p><p>Visit our <a href=${intl.formatMessage(
        { id: "links.documentation.create_new_project" }
      )} target="_blank" rel="noopener noreferrer">docs</a> for more information.</p>`,
      copyCode: [
        {
          label: "Set Git remote using the CLI",
          code: [`${CLI_COMMAND_NAME} project:set-remote <project ID>`]
        },
        {
          label: "Set Git remote using Git",
          code: [
            `git remote add ${CLI_COMMAND_NAME} <project ID>@git.<project REGION>:<project ID>.git`
          ]
        }
      ]
    },
    {
      id: "yaml",
      label: "Configure your project",
      title: `To deploy your project you must first add ${APP_NAME} configuration YAML files`,
      required: true,
      bodyText: `<p>You can start by creating empty YAML files. In the next steps we will help you fill them in.</p><p><a href=${intl.formatMessage(
        { id: "links.documentation.yaml_configuration" }
      )} target="_blank" rel="noopener noreferrer">Learn more</a> about configuring the YAML files</p>`,
      copyCode: [
        {
          label: "Application code structure",
          code: [
            ` ├── ${configurationFolder}`,
            " │   ├── routes.yaml",
            " │   └── services.yaml",
            ` ├── ${intl.formatMessage({
              id: "app_config_file"
            })}`,
            " └── < application code >"
          ],
          codeType: "noCopy"
        },
        {
          label: "Create empty files",
          code: [
            `mkdir ${configurationFolder}/`,
            `touch ${intl.formatMessage({
              id: "app_routes_file_path"
            })}`,
            `touch ${intl.formatMessage({
              id: "app_services_file_path"
            })}`,
            `touch ${intl.formatMessage({
              id: "app_config_file"
            })}`
          ]
        }
      ]
    },
    {
      id: "services",
      label: "Configure services",
      title: "Configure your services",
      required: false,
      bodyText: `<p>${APP_NAME} comes with fully managed services with databases such as MySQL or Postgres, caches like Redis or Varnish and search-engines like Solr. You can leave this blank for now and configure them later or use the dropdown to compose your <code>services.yaml</code> file with code examples. Your sample file will include the latest versions we offer.</p><p><a href=${intl.formatMessage(
        { id: "links.documentation.services" }
      )} target="_blank" rel="noopener noreferrer">Learn more</a> about configuring your services.</p>`
    },
    {
      id: "application",
      label: "Configure applications",
      title: "Configure your application",
      required: true,
      bodyText: `<p>The <code>${intl.formatMessage({
        id: "app_config_file"
      })}</code> file defines how your application is built, deployed and run. You should define at a minimum: a unique <code>name</code>, <code>type</code> (the runtime), the size of your persistent <code>disk</code> and <code>web</code>.</p><p>If you configured services in the previous step, pay attention to how those services are defined in the <code>relationships</code> block.</p><p>Take a look at our <a href=${intl.formatMessage(
        { id: "links.documentation.container_configuration" }
      )} target="_blank" rel="noopener noreferrer">documentation</a> for more detailed examples and for instructions on deploying multiple applications and workers in your cluster.</p>`
    },
    {
      id: "routes",
      label: "Configure routes",
      title: "Configure the HTTP router",
      required: true,
      bodyText: `<p>${APP_NAME} supports multiple applications in a single cluster as well as advanced caching and redirects. The <code>${intl.formatMessage(
        {
          id: "app_routes_file_path"
        }
      )}</code> file defines those. The example below routes all the requests to a single application and redirects the <pre>www.</pre> subdomain to the root domain.</p><p><a href=${intl.formatMessage(
        {
          id: "links.documentation.routes"
        }
      )} target="_blank" rel="noopener noreferrer">Learn more</a> about configuring your routes.</p>`
    },
    {
      id: "push",
      label: "Commit & push",
      title: `Commit and push your configuration files to ${APP_NAME}`,
      required: true,
      bodyText: `<p>To deploy on ${APP_NAME} add the required configuration files to your Git repository: </p><p><label>Required files:</label><br><a href=${intl.formatMessage(
        { id: "links.documentation.container_configuration" }
      )} target="_blank" rel="noopener noreferrer"><code>${intl.formatMessage({
        id: "app_config_file"
      })}</code></a>, <a href=${intl.formatMessage({
        id: "links.documentation.services"
      })} target="_blank" rel="noopener noreferrer"><code>${intl.formatMessage({
        id: "app_services_file_path"
      })}</code></a>, and <a href=${intl.formatMessage({
        id: "links.documentation.routes"
      })} target="_blank" rel="noopener noreferrer"><code>${intl.formatMessage({
        id: "app_routes_file_path"
      })}</code></a></p><p>Then commit and push them using the following commands:</p><br><p>See our <a href=${intl.formatMessage(
        { id: "links.documentation.own_code_project_configuration" }
      )} target="_blank" rel="noopener noreferrer">documentation</a> for more information.</p>`,
      copyCode: [
        {
          label: "Commit and push",
          code: [
            "git add .",
            'git commit -m "Add config files."',
            `git push -u ${CLI_COMMAND_NAME} ${mainBranch}`
          ],
          multiLine: true
        },
        {
          label: "Verify deployment",
          code: [`${CLI_COMMAND_NAME} url`]
        }
      ]
    },
    {
      id: "database",
      label: "Import existing database",
      title: `Import an existing database to ${APP_NAME}`,
      required: false,
      bodyText: `<p>If you run <a href=${intl.formatMessage({
        id: "links.documentation.mysql_import_data"
      })} target="_blank" rel="noopener noreferrer">MySQL</a> or <a href=${intl.formatMessage(
        {
          id: "links.documentation.postgres_import_data"
        }
      )} target="_blank" rel="noopener noreferrer">Postgres</a>, you can use the CLI’s <code>${CLI_COMMAND_NAME} sql</code> command to pipe a database dump onto the ${APP_NAME} database.</p><p>Check our <a href=${intl.formatMessage(
        {
          id: "links.documentation.migrating_tutorial"
        }
      )}>documentation</a> to find-out more about data migration.</p>`,
      copyCode: [
        {
          label: "Run the commands",
          code: [`${CLI_COMMAND_NAME} sql < my_database_snapshot.sql`]
        }
      ]
    },
    {
      id: "files",
      label: "Import additional files",
      title: "Import additional files",
      required: false,
      bodyText: `<p>${APP_NAME} supports persistent mounts to which your applications can write. These are defined in the <code>${intl.formatMessage(
        {
          id: "app_config_file"
        }
      )}</code> file.</p><p>If you have existing files you want to upload the CLI can guide you through the process. </p>`,
      copyCode: [
        {
          label: "Import files",
          code: [
            `${CLI_COMMAND_NAME} mount:upload --mount web/uploads --source ./local_files`
          ]
        }
      ]
    }
  ];
};

export default getDefaultBlankWizardSteps;
